import { useEffect, useRef } from 'react';
import { shouldGridHandleKey } from './dbGridKeyGuard';

/**
 * Ctrl+R / Cmd+R / F5 in the DB Manager refreshes the table instead of
 * reloading the whole app (owner, 30.09.2026: "ctrl + r while in DB manager
 * should … trigger the existing refresh action").
 *
 * Nothing handled the key before, so the webview reloaded and every tab,
 * connection and unsaved edit in the app was gone. Now, while a DB Manager
 * tab is on screen (this hook lives in DbManagerResultsTab, which only mounts
 * then):
 *   - table tab: the existing Refresh (`refresh`), under its own rule — with
 *     staged edits it does nothing and says why (`onBlocked`), never discards;
 *   - query tab: the reload is still blocked, nothing runs;
 *   - a modal is open: the reload is blocked, the table is left alone.
 * Capture phase, so Monaco (which stops keys on its own node) cannot swallow it.
 *
 * state: { isQueryTab, blocked (staged edits), busy (loading) }
 */
export default function useDbRefreshShortcut({ isQueryTab, blocked, busy, refresh, onBlocked }) {
  const latest = useRef(null);
  latest.current = { isQueryTab, blocked, busy, refresh, onBlocked };

  useEffect(() => {
    const onKey = (e) => {
      const isReload = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key || '').toLowerCase() === 'r');
      if (!isReload) return;
      e.preventDefault(); // never reload the app from the DB Manager
      e.stopPropagation();
      const s = latest.current;
      if (!shouldGridHandleKey(e) || s.isQueryTab || s.busy || e.repeat) return;
      if (s.blocked) {
        s.onBlocked?.();
        return;
      }
      s.refresh();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
}
